import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import './Dashboard.css';
import { registerLicense } from '@syncfusion/ej2-base';
import { MultiSelectComponent } from '@syncfusion/ej2-react-dropdowns';
import { useAuth } from '../../contexts/AuthContext';
import { useRoleMessages } from '../../contexts/RoleMessagesContext';
import { getSyncfusionMultiSelectA11yHandlers } from '../../utils/syncfusionMultiSelectA11y';
import { storeRefreshedTokens } from '../../utils/authTokens';
// Syncfusion styles
import '@syncfusion/ej2-base/styles/material.css';
import '@syncfusion/ej2-buttons/styles/material.css';
import '@syncfusion/ej2-inputs/styles/material.css';
import '@syncfusion/ej2-react-dropdowns/styles/material.css';

// Register Syncfusion license from env if provided
if (process.env.REACT_APP_SYNCFUSION_LICENSE) {
  try { registerLicense(process.env.REACT_APP_SYNCFUSION_LICENSE); } catch (_) { }
}

// Race categories aligned to reference UI
const RACE_OPTIONS = [
  'American Indian, Alaska Native or Indigenous',
  'Hispanic or Latino, Latina or Latine',
  'Native Hawaiian or Other Pacific Islander',
  'South Asian',
  'Southeast Asian',
  'East Asian',
  'West Asian',
  'Black or African American',
  'White/Caucasian',
  'Prefer not to answer'
];

const AGE_GROUPS = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'];

const DISABILITY_OPTIONS = [
  'ADHD', 'Arthritis', 'Autism Spectrum Disorder (ASD)', 'Auto Inflammatory Disorders', 'Autoimmune', 'Blindness / Vision Loss', 'Blood Related', 'Cancer', 'Cardiovascular', 'Cerebral Palsy',
  'Chronic Fatigue / Energy Limiting Disability', 'Chronic Illness', 'Chronic Pain/Migraine', 'Cognitive', 'Communication Disability', 'Deafness', 'Depression', 'Developmental Disabilities', 'Diabetes', 'Digestive', 'Down Syndrome', 'Dyslexia',
  'Endocrine', 'Environmental / Chemical Sensitivity', 'Hearing Loss', 'Infectious Disease-related Disability', 'Intellectual Disability', 'Learning Disability (Beyond Dyslexia)', 'Limb Diff / Amputee', 'Mental Health', 'Mobility Disability', 'Multiple Sclerosis', 'Musculoskeletal', 'Neurodivergent', 'Neurological',
  'Paralysis', 'Post Traumatic Stress', 'Psychiatric Disabilities', 'Rare Disease / Genetic Condition', 'Respiratory', 'Sensory Processing Disorder', 'Skin', 'Speech', 'Traumatic Brain Injury'
];

// Comprehensive country list (display names)
const COUNTRIES_RAW = [
  'United States', 'Canada', 'United Kingdom', 'Australia', 'New Zealand', 'Ireland', 'France', 'Germany', 'Italy', 'Spain', 'Portugal', 'Netherlands', 'Belgium', 'Switzerland', 'Austria', 'Sweden', 'Norway', 'Denmark', 'Finland', 'Iceland', 'Poland', 'Czech Republic', 'Hungary', 'Romania', 'Bulgaria', 'Croatia', 'Slovenia', 'Slovakia', 'Estonia', 'Latvia', 'Lithuania', 'Greece', 'Turkey', 'Cyprus', 'Russia', 'Ukraine', 'Belarus', 'Moldova', 'Georgia', 'Armenia', 'Azerbaijan', 'Kazakhstan', 'Uzbekistan', 'Turkmenistan', 'Tajikistan', 'Kyrgyzstan', 'China', 'Hong Kong', 'Taiwan', 'Japan', 'South Korea', 'Mongolia', 'India', 'Pakistan', 'Bangladesh', 'Sri Lanka', 'Nepal', 'Bhutan', 'Maldives', 'Myanmar', 'Thailand', 'Laos', 'Cambodia', 'Vietnam', 'Malaysia', 'Singapore', 'Indonesia', 'Philippines', 'Brunei', 'East Timor', 'United Arab Emirates', 'Saudi Arabia', 'Qatar', 'Bahrain', 'Kuwait', 'Oman', 'Yemen', 'Iran', 'Iraq', 'Jordan', 'Lebanon', 'Israel', 'Palestine', 'Syria', 'Egypt', 'Morocco', 'Algeria', 'Tunisia', 'Libya', 'Sudan', 'South Sudan', 'Ethiopia', 'Eritrea', 'Djibouti', 'Somalia', 'Kenya', 'Uganda', 'Tanzania', 'Rwanda', 'Burundi', 'DR Congo', 'Republic of the Congo', 'Gabon', 'Equatorial Guinea', 'Cameroon', 'Nigeria', 'Ghana', 'Benin', 'Togo', 'Burkina Faso', 'Mali', 'Niger', 'Chad', 'Central African Republic', 'Guinea', 'Guinea-Bissau', 'Sierra Leone', 'Liberia', 'Côte d\'Ivoire', 'Cape Verde', 'Senegal', 'Gambia', 'Mauritania', 'Western Sahara', 'Ecuador', 'Colombia', 'Venezuela', 'Guyana', 'Suriname', 'Brazil', 'Peru', 'Bolivia', 'Paraguay', 'Uruguay', 'Argentina', 'Chile', 'Mexico', 'Guatemala', 'Belize', 'Honduras', 'El Salvador', 'Nicaragua', 'Costa Rica', 'Panama', 'Cuba', 'Jamaica', 'Haiti', 'Dominican Republic', 'Trinidad and Tobago', 'Barbados', 'Bahamas', 'Antigua and Barbuda', 'Dominica', 'Grenada', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Saint Kitts and Nevis', 'Greenland', 'Iraq', 'Iran', 'Afghanistan', 'Fiji', 'Papua New Guinea', 'Solomon Islands', 'Vanuatu', 'Samoa', 'Tonga', 'Kiribati', 'Tuvalu', 'Nauru', 'Palau', 'Marshall Islands', 'Micronesia', 'French Polynesia', 'New Caledonia', 'Réunion', 'Seychelles', 'Mauritius', 'Madagascar', 'Mozambique', 'Zimbabwe', 'Zambia', 'Malawi', 'Botswana', 'Namibia', 'Angola', 'Lesotho', 'Eswatini', 'Qatar', 'Bahrain', 'Malta', 'Luxembourg', 'Monaco', 'Liechtenstein', 'Andorra', 'San Marino', 'Vatican City', 'Other'
];
// Remove duplicates and sort alphabetically
const COUNTRIES = Array.from(new Set(COUNTRIES_RAW)).sort((a, b) => a.localeCompare(b));

const SurveyForm = React.forwardRef(function SurveyForm({ onValidationChange, embedded, onSaveSuccess }, ref) {
  const { user } = useAuth();
  const { getMessage } = useRoleMessages();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  const announceTimeoutRef = useRef(null);
  const infoBannerMessage = getMessage('survey', 'info-banner') || '';
  const [form, setForm] = useState({
    race: [],
    genderIdentity: '',
    ageGroup: '',
    countryOfOrigin: 'United States',
    disabilities: [],
    otherDisability: ''
  });

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: '', type }), 2500);
  };

  const announceForScreenReader = (message) => {
    if (announceTimeoutRef.current) {
      clearTimeout(announceTimeoutRef.current);
    }
    // Clear then reset so repeated identical messages are still announced.
    setLiveAnnouncement('');
    announceTimeoutRef.current = setTimeout(() => {
      setLiveAnnouncement(message);
    }, 30);
  };

  const focusMainContent = () => {
    const mainContent = document.getElementById('main-content') || document.querySelector('main');
    if (!mainContent) return;
    mainContent.setAttribute('tabindex', '-1');
    mainContent.focus();
  };

  const scrollToTop = () => {
    const scrollContainer =
      document.getElementById('main-content') ||
      document.querySelector('.dashboard-main') ||
      document.querySelector('main');
    if (scrollContainer && typeof scrollContainer.scrollTo === 'function') {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Always read the latest token from storage
  const getToken = () => localStorage.getItem('token');
  const getRefreshToken = () => localStorage.getItem('refreshToken');

  const tryRefreshToken = async () => {
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) return false;
      const res = await axios.post('/api/auth/refresh', { refreshToken });
      // Skips the write if the user logged out or switched accounts while this was in flight
      return storeRefreshedTokens(refreshToken, res.data?.tokens);
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const res = await axios.get('/api/auth/survey', {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        if (res.data?.survey) {
          setForm({
            race: res.data.survey.race || [],
            genderIdentity: res.data.survey.genderIdentity || '',
            ageGroup: res.data.survey.ageGroup || '',
            countryOfOrigin: res.data.survey.countryOfOrigin || 'United States',
            disabilities: res.data.survey.disabilities || [],
            otherDisability: res.data.survey.otherDisability || ''
          });
        }
      } catch (e) {
        if (e?.response?.status === 401) {
          const refreshed = await tryRefreshToken();
          if (refreshed) {
            // retry once
            try {
              const res2 = await axios.get('/api/auth/survey', {
                headers: { Authorization: `Bearer ${getToken()}` }
              });
              if (res2.data?.survey) {
                setForm({
                  race: res2.data.survey.race || [],
                  genderIdentity: res2.data.survey.genderIdentity || '',
                  ageGroup: res2.data.survey.ageGroup || '',
                  countryOfOrigin: res2.data.survey.countryOfOrigin || 'United States',
                  disabilities: res2.data.survey.disabilities || [],
                  otherDisability: res2.data.survey.otherDisability || ''
                });
              }
              return;
            } catch (_) { }
          }
        }
        showToast('Failed to load survey', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchSurvey();
  }, []);

  useEffect(() => {
    return () => {
      if (announceTimeoutRef.current) {
        clearTimeout(announceTimeoutRef.current);
      }
    };
  }, []);


  const persistPartial = async (partial) => {
    try {
      await axios.put('/api/auth/survey', partial, { headers: { Authorization: `Bearer ${getToken()}` } });
      showToast('Saved', 'success');
      return true;
    } catch (e) {
      if (e?.response?.status === 401) {
        const refreshed = await tryRefreshToken();
        if (refreshed) {
          try {
            await axios.put('/api/auth/survey', partial, { headers: { Authorization: `Bearer ${getToken()}` } });
            showToast('Saved', 'success');
            return true;
          } catch (_) { }
        }
      }
      showToast('Failed to save', 'error');
      return false;
    }
  };

  const onToggleArray = async (key, value) => {
    if (key === 'disabilities') {
      // Compute next list from current state to avoid relying on async updater timing
      const current = Array.isArray(form.disabilities) ? form.disabilities : [];
      const set = new Set(current);
      if (set.has(value)) set.delete(value); else set.add(value);
      const nextList = Array.from(set);
      // optimistic update
      setForm(prev => ({ ...prev, disabilities: nextList }));
      const ok = await persistPartial({ disabilities: nextList });
      if (!ok) {
        // revert to previous
        setForm(prev => ({ ...prev, disabilities: current }));
      }
      return;
    }

    // Generic array toggle for other keys if needed in future
    setForm(prev => {
      const arr = new Set(prev[key] || []);
      if (arr.has(value)) arr.delete(value); else arr.add(value);
      return { ...prev, [key]: Array.from(arr) };
    });
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    // Trigger validation callback when form changes
    if (onValidationChange) {
      setTimeout(onValidationChange, 0);
    }
  };

  const saveSurvey = async () => {
    setSaving(true);
    try {
      await axios.put('/api/auth/survey', form, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      announceForScreenReader('Survey saved');
      if (!embedded) showToast('Survey saved', 'success');
      if (!embedded) {
        focusMainContent();
      }
      scrollToTop();
      if (onSaveSuccess) {
        onSaveSuccess();
      }
      return true;
    } catch (e) {
      if (e?.response?.status === 401) {
        const refreshed = await tryRefreshToken();
        if (refreshed) {
          try {
            await axios.put('/api/auth/survey', form, {
              headers: { Authorization: `Bearer ${getToken()}` }
            });
            announceForScreenReader('Survey saved');
            if (!embedded) showToast('Survey saved', 'success');
            if (!embedded) {
              focusMainContent();
            }
            scrollToTop();
            if (onSaveSuccess) {
              onSaveSuccess();
            }
            setSaving(false);
            return true;
          } catch (_) { }
        }
      }
      announceForScreenReader('Failed to save survey');
      if (!embedded) showToast('Failed to save survey', 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveSurvey();
  };

  useImperativeHandle(ref, () => ({ save: saveSurvey }));

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner" />
        <p>Loading survey...</p>
      </div>
    );
  }

  return (
    <>
      {!embedded && (
        <Helmet>
          <title>Survey - abilityconnect</title>
        </Helmet>
      )}
      <div className={`dashboard-content survey-page-content${embedded ? ' survey-page-content-embedded' : ''}`}>
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">{liveAnnouncement}</div>
        {toast.visible && (
          <div className={`toast ${toast.type}`} role="status" aria-live="polite">{toast.message}</div>
        )}
        {!embedded && <h1>Survey – Tell us about yourself</h1>}
      
      {infoBannerMessage && (
        <div className="info-banner">
          <span>{infoBannerMessage}</span>
        </div>
      )}

      <form aria-label="Survey form" className="account-form survey-form" onSubmit={handleSubmit}>
        <div className="form-row form-row-3">
          <div className="form-group">
            <label id="race-label">Race (select all that apply)</label>
            <span id="race-instructions" className="sr-only">
              Select one or more races. Use the left and right arrow to navigate selections. Race selection is optional.
            </span>
            <MultiSelectComponent
              id="race"
              aria-labelledby="race-label"
              aria-describedby="race-instructions"
              dataSource={RACE_OPTIONS}
              value={form.race}
              mode="Box"
              placeholder="Select race"
              enableSelectionOrder={false}
              cssClass="ajf-input"
              showDropDownIcon={true}
              popupHeight="260px"
              allowFiltering={false}
              {...getSyncfusionMultiSelectA11yHandlers({
                inputId: 'race',
                labelId: 'race-label',
                instructionsId: 'race-instructions',
                listboxLabel: 'Race options',
                fieldLabel: 'Race',
                onChange: (args) => {
                  const values = Array.isArray(args?.value) ? args.value : [];
                  setForm(prev => ({ ...prev, race: values }));
                  if (onValidationChange) {
                    setTimeout(onValidationChange, 0);
                  }
                },
              })}
            />
            <div className="field-help">Race selection is optional</div>
          </div>
          <div className="form-group">
            <label htmlFor="genderIdentity">I identify my gender as </label>
            <select
              id="genderIdentity"
              name="genderIdentity"
              value={form.genderIdentity}
              onChange={onChange}
              aria-describedby="gender-help"
            >
              <option value="">Select gender</option>
              <option value="Female">Female</option>
              <option value="Male">Male</option>
              <option value="Non-binary">Non-binary</option>
              <option value="Prefer not to say">Prefer not to say</option>
              <option value="Other">Other</option>
            </select>
            <div id="gender-help" className="field-help">Gender identity is optional</div>
          </div>
          <div className="form-group">
            <label htmlFor="ageGroup">Age Group </label>
            <select
              id="ageGroup"
              name="ageGroup"
              value={form.ageGroup}
              onChange={onChange}
              aria-describedby="age-help"
            >
              <option value="">Select age group</option>
              {AGE_GROUPS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <div id="age-help" className="field-help">Age group is optional</div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label htmlFor="countryOfOrigin">Country of Origin </label>
            <select
              id="countryOfOrigin"
              name="countryOfOrigin"
              value={form.countryOfOrigin}
              onChange={onChange}
              aria-describedby="country-origin-help"
            >
              {COUNTRIES.map((c, idx) => (
                <option key={`${c}-${idx}`} value={c}>{c}</option>
              ))}
            </select>
            <div id="country-origin-help" className="field-help">Country of origin is optional</div>
          </div>
        </div>

        <fieldset className="disability-fieldset">
          <legend>Disability Information</legend>
          <p className="fieldset-description">
            Indicate current disability(ies). Select all that apply. If these choices don&apos;t apply, use the &quot;other&quot; space below.
          </p>

          <div className="checkbox-grid">
            {DISABILITY_OPTIONS.map(opt => (
              <label key={opt} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.disabilities.includes(opt)}
                  onChange={() => onToggleArray('disabilities', opt)}
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>

          <div className="form-group">
            <label htmlFor="otherDisability">Other disability</label>
            <input id="otherDisability" name="otherDisability" type="text" value={form.otherDisability} onChange={onChange} />
          </div>

          <div className="disability-clear-all">
            <button
              type="button"
              className="update-button"
              aria-label="Clear all options"
              onClick={async () => {
                const prev = form.disabilities;
                setForm(p => ({ ...p, disabilities: [] }));
                announceForScreenReader('Clear all options');
                const ok = await persistPartial({ disabilities: [] });
                if (!ok) {
                  setForm(p => ({ ...p, disabilities: prev }));
                  announceForScreenReader('Unable to clear all options');
                }
              }}
            >
              Clear All
            </button>
          </div>
        </fieldset>

        {!embedded && (
          <button type="submit" className="update-button" disabled={saving}>
            {saving ? 'Saving…' : 'Save Survey'}
          </button>
        )}
      </form>
      </div>
    </>
  );
});

export default SurveyForm;
